/**
 * @fileoverview @fileoverview Defines the `AgenticContextProvider` class, which implements the `ContextProvider` interface. It uses an AI model to dynamically select and read relevant files based on a user's prompt, returning a `FolderContext` with the selected file contents. Key functions include `getContext` and `parseFilesToRead`.
 */

import { readFileSync } from 'fs'
import { join } from 'path'
import { Model } from '@iterator/shared-node/ai/llm_types'
import { ContextProvider, ContextProviderFn, FolderContext } from './context_types'
import { getFileHierarchy } from './folder'
import { print_ai } from '@iterator/shared-node/util/logging'
import { AiStreamer } from './ai_streamer'
import { baseOfSparsePath, SparsePath } from '@iterator/shared-node/util/sparse_path'

/**
 * This context provider has a model choose what will be helpful to the user.
 *
 * Steps:
 * 1. Get directory structure
 * 2. Loop until LLM decides it has enough context:
 *   - LLM picks a file to read and writes a note about how it's relevant to the user's prompt
 * return texts with:
 * <file path="path/to/file.ts">
 * <note_on_usage>
 * ...
 * </note_on_usage>
 * <code_from_file>
 * ...
 * </code_from_file>
 * </file>
 */
export class AgenticContextProvider implements ContextProvider {
  constructor(private model: Model) {}

  shouldThink(): boolean {
    return false
  }

  getContext: ContextProviderFn = async (
    directory: SparsePath,
    prompt?: string,
  ): Promise<FolderContext> => {
    const folderContext = `<all_files>\n${getFileHierarchy(directory)}</all_files>`
    const aiStreamer = new AiStreamer()
    const { message } = await this.model
      .getApi()
      .streamChatCompletions(
        SYSTEM_PROMPT,
        [folderContext],
        [{ role: 'user', content: `<coder_prompt>\n${prompt}\n</coder_prompt>` }],
        (chunk) => aiStreamer.stream(chunk),
      )
    print_ai('')

    let filesToRead = parseFilesToRead(message.content)
    let filesRead = []
    let loopCount = 0
    while (filesToRead.length > 0 && loopCount < 4) {
      for (const file of filesToRead) {
        const fileContents = readFileSync(join(baseOfSparsePath(directory), file))
        filesRead.push({ path: file, contents: fileContents })
      }
      print_ai('<starting new loop>')
      const { message } = await this.model
        .getApi()
        .streamChatCompletions(
          SYSTEM_PROMPT,
          [
            folderContext,
            ...filesRead.map((file) => `<file path="${file.path}">\n${file.contents}\n</file>`),
          ],
          [{ role: 'user', content: `<coder_prompt>\n${prompt}\n</coder_prompt>` }],
          (chunk) => aiStreamer.stream(chunk),
        )
      print_ai('')
      filesToRead = parseFilesToRead(message.content)
      loopCount++
    }

    return Promise.resolve({
      system: folderContext,
      texts: filesRead.map(
        (file) =>
          `<file path="${join(baseOfSparsePath(directory), file.path)}">\n${file.contents}\n</file>`,
      ),
    })
  }
}

function parseFilesToRead(message: string): string[] {
  const regex = /<read_file path="([^"]+)" \/>/g
  const matches = Array.from(message.matchAll(regex))
  return matches.map((match) => match[1]!)
}

const SYSTEM_PROMPT = `DO NOT GIVE ME HIGH LEVEL SHIT!!! I DON'T WANT "Here's how you can blablabla

Your task is to pick relevant files that might be important for a coder when implementing a task. You will be provided with a few different blocks (do NOT output blocks of this type):
- <all_files> is the list of all files you could read
- <coder_prompt> is what the coder is trying accomplish 
- <file path='...'> includes the contents of any files you have read so far.

In your response, if there are any files that you wish to read output as follows:
<read_file path="...." />

Output <read_file> block and nothing else. Unless no more files need to be read, then output "No more files to read."

DO NOT OUTPUT ANYTHING ELSE.`
